// CR09 — Checklist domain types (dashboard). Field shapes mirror the backend API
// (snake_case) to avoid lossy mapping.

export type ChecklistStatus = 'draft' | 'published' | 'archived';
export type ChecklistItemType = 'yes_no' | 'quantity' | 'amount' | 'text' | 'date';

export const CHECKLIST_CHANNELS = ['Retail', 'MTL', 'POS', 'Other'] as const;
export const CHECKLIST_ASSIGNMENTS = ['Supervisor', 'Developer', 'Sales Staff', 'Other'] as const;

export const ITEM_TYPE_LABELS: Record<ChecklistItemType, string> = {
  yes_no: 'نعم / لا',
  quantity: 'كمية',
  amount: 'مبلغ',
  text: 'نص',
  date: 'تاريخ'
};

export const STATUS_LABELS: Record<ChecklistStatus, string> = {
  draft: 'مسودة',
  published: 'منشورة',
  archived: 'مؤرشفة'
};

export interface ChecklistItem {
  id?: number;
  item_name: string;
  item_type: ChecklistItemType;
  mandatory: boolean;
  display_order: number;
  active: boolean;
}

export interface Checklist {
  id: number;
  name: string;
  description: string | null;
  channel: string;
  assignment_type: string;
  status: ChecklistStatus;
  effective_date: string | null;
  items?: ChecklistItem[];
  created_at: string;
  updated_at: string;
}

/** Payload sent to POST / PUT (matches backend Create/Update DTO). */
export interface ChecklistPayload {
  name: string;
  description?: string | null;
  channel: string;
  assignment_type: string;
  effective_date?: string | null;
  items: Array<{
    item_name: string;
    item_type: ChecklistItemType;
    mandatory: boolean;
    display_order: number;
    active: boolean;
  }>;
}

export interface ChecklistFilters {
  status?: ChecklistStatus;
  channel?: string;
  assignment_type?: string;
  search?: string;
}

/** A blank item row for the editor. */
export function emptyChecklistItem (order: number): ChecklistItem {
  return {
    item_name: '',
    item_type: 'yes_no',
    mandatory: false,
    display_order: order,
    active: true
  };
}

// ── Per-visit submissions (CR09 Phase 2, read-only dashboard view) ──────────────
// Mirrors the backend VisitChecklistSubmissionResponse: submissions filled during
// a visit, enriched with the checklist name + each answer's item name/type.

export type SubmissionStatus = 'draft' | 'in_progress' | 'completed' | 'submitted';

export const SUBMISSION_STATUS_LABELS: Record<SubmissionStatus, string> = {
  draft: 'مسودة',
  in_progress: 'قيد التعبئة',
  completed: 'مكتملة',
  submitted: 'مُرسلة'
};

export interface VisitSubmissionAnswer {
  checklist_item_id: number;
  item_name: string;
  item_type: ChecklistItemType;
  value: string | null;
}

export interface VisitChecklistSubmission {
  id: number | null;
  checklist_id: number;
  checklist_name: string;
  status: SubmissionStatus;
  submitted_at: string | null;
  created_at?: string;
  answers: VisitSubmissionAnswer[];
}
